import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import { IoBuild } from 'react-icons/io5';
import { Tooltip } from 'react-tooltip';
import { getArea } from 'ol/sphere';

import { useAuth } from '@contexts/User.context';
import { useProject } from '@contexts/Project.context';
import { PROJECT_MODE } from '@utils/constants';

import {
	setupFreehandDraw,
	removeFreehandDraw,
	checkAreaOfInterest,
} from '@utils/map/freeHand.draw';
import { clearTooltip } from '@utils/map/tooltip.overlay';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import { formatArea } from '@utils/measurementSystem';

import { getGrid } from '@utils/api';
import AnnotateOrthophoto from '../ai/AnnotateOrthophoto';
import AnnotateSingleImages from '../ai/AnnotateSingleImages';
import { resetAnnotationRefs } from '@utils/map/annotation/annotation.interaction';

const annotationSidebarId = 'annotationSidebar';

export default function Annotate() {
	const {
		project,
		projectMode,
		detections,
		pickedTask,
		toolBarVisible,
		isDemo,
		mapObject,
		mapTooltip,
		annotationMode,
		setActiveSidebar,
		removeFromActiveSidebars,
		selectedSingleImage,
		dispatch,
	} = useProject();

	const {
		tierTrial,
		trialEnded,
		tierPremium,
		tierPro,
		subscriptionActive,
		isEnterprise,
		activeOrgAccess,
		userAccess,
	} = useAuth();

	const { tooltip, tooltipRef } = mapTooltip || {};

	// layers to hide during annotation
	const layersToHide = [
		'aoiLayer',
		'dsmLayer',
		'hexagonLayer',
		'trainingDataLayer',
		'centerLayer',
		'outlineLayer',
		'detailsLayer',
	];

	// layers to show after annotation is stopped
	const [layersToShow, setLayersToShow] = useState([]);

	const getLayersToShow = () => {
		const layers = mapObject.getLayers()?.getArray();
		if (!layers?.length) return [];

		// Gets an array of customLayerId strings that are visible
		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const annotationButtonTooltipText = () => {
		if (tierTrial) {
			if (trialEnded) return 'Your trial has ended and you can no longer annotate';
			if (pickedTask?.multimodel) return 'You can not train a multimodel as a trial user';
			if (pickedTask?.is_trained) return 'You can not improve accuracy as a trial user';
		}

		if (detections.length === 0) return 'Make manual annotations to train the AI model';

		return 'Enhance AI accuracy by fine-tuning the annotations';
	};

	const checkTrainingPermissions = async () => {
		const showAlert = msg => !!alert(msg);

		if (tierTrial) {
			if (trialEnded) return showAlert('Your trial has ended and you can no longer annotate');

			if (pickedTask?.multimodel)
				return showAlert('You cannot train a multimodel as a trial user');

			if (pickedTask?.is_trained)
				return showAlert('You cannot improve accuracy as a trial user');

			// Use mapObject.getLayers() to check if training data layer exists
			const layers = mapObject.getLayers()?.getArray();
			const trainingDataLayerExists = layers?.some(
				layer => layer.get('name') === 'Training data'
			);

			if (trainingDataLayerExists)
				return showAlert(
					'Training data exists. You can only train a model once as a trial user.'
				);
		}

		if (tierPremium && !userAccess?.includes('train'))
			return showAlert('Your user does not have access rights to train AI');

		if (tierPro && !subscriptionActive)
			return showAlert('Your user needs an active subscription to train AI');

		if (isEnterprise) {
			// Check if activeOrgAccess array contains "all" or "train"
			const hasOrgAccess =
				activeOrgAccess.includes('all') || activeOrgAccess.includes('train');

			if (!hasOrgAccess)
				return showAlert('Your user does not have access rights to train AI');
		}

		return true; // Allow training if none of the conditions are met
	};

	const prepareTraining = async () => {
		const isTrainingAllowed = await checkTrainingPermissions();
		if (!isTrainingAllowed) return;

		const grid = await fetchGrid({
			project_uuid: project.uuid,
			model_uuid: pickedTask.model_uuid,
		});

		if (!grid || grid.length === 0) {
			console.error('Could not fetch grid for training');
			window.alert(
				'An error accured and training could not be started. Please try again later.'
			);
			return;
		}

		const existingLayersVisibility = getLayersToShow();
		setLayersToShow(existingLayersVisibility);

		// Hide all layers that should not be visible during annotation
		setLayersVisibilityByCustomIds(mapObject, layersToHide, false);

		dispatch({
			type: 'setToolBarVisible',
			payload: false,
		});

		dispatch({
			type: 'setInteractionsDisabled',
			payload: true,
		});

		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Set a boundary',
				body: 'Please draw a boundary around the area in the map where you want to make annotations. Press ESC to cancel.',
			},
		});

		const { draw, layer, source } = setupDraw({
			mapObject,
			tooltip,
			tooltipRef,
		});

		// Make it possible to cancel the draw by pressing ESC
		const escapeFunction = e => {
			if (e.key === 'Escape') {
				cleanupDraw({ draw, layer });
				dispatch({
					type: 'setToolBarVisible',
					payload: true,
				});
				setLayersVisibilityByCustomIds(
					mapObject,
					existingLayersVisibility, // Not using layersToShow here because state is not up to date.
					true
				);

				// Remove the event listener after it has been used
				window.removeEventListener('keydown', escapeFunction);
			}
		};
		window.addEventListener('keydown', escapeFunction);

		// Draw the area of interest
		// Recursively run the promise until it returns true
		const drawAreaUntilTrue = async () => {
			const gridFeatures = await drawAOI({
				grid,
				draw,
				source,
				mapTooltip,
				mode: projectMode,
			});
			if (!gridFeatures) {
				await drawAreaUntilTrue(); // Recurse
				return;
			}

			// Clear the draw interaction and source
			cleanupDraw({ draw, layer });
			// Remove the event listener bc it's no longer needed
			window.removeEventListener('keydown', escapeFunction);

			// Allow training to start
			allowTraining(gridFeatures);
		};
		drawAreaUntilTrue();
	};

	const allowTraining = gridFeatures => {
		const taskLabels = pickedTask.classes.map(task => task.description);
		const taskIds = Array.from(Array(taskLabels.length).keys());

		dispatch({
			type: 'setAnnotationMode',
			payload: {
				gridFeatures,
			},
		});

		dispatch({
			type: 'setAnnotationSidebarData',
			payload: {
				classIds: taskIds,
				labels: taskLabels,
				activeClassId: taskIds[0],
			},
		});

		setActiveSidebar({ sidebarId: annotationSidebarId });
	};

	const cleanupDraw = ({ draw, layer }) => {
		// Clear the draw interaction and source
		dispatch({
			type: 'setDialogue',
			payload: null,
		});

		dispatch({
			type: 'setInteractionsDisabled',
			payload: false,
		});

		removeFreehandDraw({ mapRef: mapObject, draw, layer });
		clearTooltip({ ...mapTooltip });

		// Reset any annotation references that may have been set before
		resetAnnotationRefs();
	};

	if (!mapObject || !pickedTask || selectedSingleImage) return null;

	if (!!annotationMode) {
		if (projectMode === PROJECT_MODE.ORTHOPHOTO) {
			return (
				<AnnotateOrthophoto
					layersToShow={layersToShow}
					hideAnnotationSidebar={() => {
						removeFromActiveSidebars(annotationSidebarId);
					}}
				/>
			);
		} else if (projectMode === PROJECT_MODE.SINGLE_IMAGE) {
			return <AnnotateSingleImages layersToShow={layersToShow} />;
		}
	}

	return (
		<>
			<Tooltip id="annotate" variant="light" />
			<Button
				disabled={!toolBarVisible || isDemo}
				onClick={prepareTraining}
				className=" btn-dark secondary"
				data-tooltip-id="tooltip-toolbar-root"
				data-tooltip-content={annotationButtonTooltipText()}
				data-tooltip-place="top">
				<IoBuild className="m-1 mb-2" style={{ transform: 'rotate(-90deg)' }} />
				{detections.length === 0 ? 'Train AI' : 'Improve AI Accuracy'}
			</Button>
		</>
	);
}

/**
 * Formats the area of a polygon geometry feature as a human-readable string.
 * Uses the OpenLayers getArea function to calculate the area of the geometry,
 * then formats it using the measurement system set in the application.
 *
 * @param {import('ol/geom').Geometry} geometry - The OpenLayers geometry object to measure
 * @returns {string} Formatted string showing the area with appropriate units (e.g., "Area: 10.50 m²")
 */
const getAreaString = geometry => {
	const [area, unit] = formatArea(getArea(geometry));
	return `${area} ${unit}`;
};

const setupDraw = ({ mapObject, tooltip, tooltipRef }) => {
	// Setup draw interaction
	const { draw, layer, source } = setupFreehandDraw({
		type: 'Polygon',
		mapRef: mapObject,
		freehand: false,
	});

	const freehandRef = {
		draw,
		layer,
		source,
	};

	// Add drawstart event listener add tooltip to the map
	draw.on('drawstart', function (e) {
		const sketch = e.feature;

		sketch.getGeometry().on('change', function (evt) {
			const geom = evt.target;

			// Set the tooltip content to the area of the geometry
			tooltipRef.textContent = getAreaString(geom);

			const tooltipCoord = geom.getInteriorPoint().getCoordinates();
			tooltip.setPosition(tooltipCoord);
		});
	});

	return freehandRef;
};

const fetchGrid = async ({ project_uuid, model_uuid }) => {
	try {
		const { success, grid_geojson } = await getGrid({
			project_uuid,
			model_uuid,
		});
		if (success && grid_geojson?.features) {
			return grid_geojson.features;
		} else {
			return [];
		}
	} catch (error) {
		return [];
	}
};

const drawAOI = async ({ grid, draw, source, mapTooltip, mode }) => {
	return new Promise((resolve, reject) => {
		checkAreaOfInterest({ mode, grid, draw })
			.then(coordinates => {
				resolve(coordinates);
			})
			.catch(error => {
				console.warn(error);
				window.alert(error);

				// Clear the draw interaction and source
				source.clear();
				clearTooltip({ ...mapTooltip });

				resolve(false);
			});
	});
};
